import { Module, VuexModule, Mutation, Action, getModule } from 'vuex-module-decorators';
import Vue from 'vue';
import store from '@/store';

import { getAsync, postAsync, patchAsync } from '@/rpcutil';

export interface PromoNewsStatus {
    id: number,
    title: string,
    date: Date,
}

export interface PromoNewsDetail {
    id: number,
    title: string,
    content: string,
    photo: string,
    date: Date,
    companyNewsId: number,
}

export interface SystemCompanyNewsDetail {
    id: number,
    title: string,
    date: Date,
    expires: Date,
    link: string,
}

export type PromoNewsDetailStore = {
    [key: number]: PromoNewsDetail,
}

export type SystemCompanyNewsDetailStore = {
    [key: number]: SystemCompanyNewsDetail,
}

function objectizePromoNewsDetail(val: PromoNewsDetail) {
    val.date = new Date(val.date);

    return val;
}

function objectizeSystemCompanyNewsDetail(val: SystemCompanyNewsDetail) {
    val.date = new Date(val.date);
    val.expires = new Date(val.expires);
}

@Module({ name: 'promo_news', namespaced: true, dynamic: true, store: store })
class PromoNewsStore extends VuexModule {
    details: PromoNewsDetailStore = {};
    status: PromoNewsStatus[] = [];
    detail_status_map: { [key: number]: number } = {};
    status_whole: boolean = false;

    company_details: SystemCompanyNewsDetailStore = {};

    @Mutation
    UPDATE_DETAIL(val: PromoNewsDetail) {
        Vue.set(this.details, val.id, val);
        console.log(val);
        if(this.detail_status_map[val.id] === undefined) {
            this.detail_status_map[val.id] = this.status.length;
        }

        Vue.set(this.status, this.detail_status_map[val.id], val);
    }

    @Mutation
    UPDATE_STATUS(val: PromoNewsStatus[]) {
        for(let i = 0; i < val.length; i++) {
            Vue.set(this.status, i, val[i]);
            this.detail_status_map[val[i].id] = i;
        }
        const delete_count = this.status.length - val.length;
        for(let i = 0; i < delete_count; i++) {
            Vue.delete(this.status, val.length);
        }
        this.status_whole = true;
    }

    @Mutation
    UPDATE_COMPANY_DETAIL(val: SystemCompanyNewsDetail) {
        Vue.set(this.company_details, val.id, val);
    }

    @Action({ rawError: true })
    async updateStatus() {
        const details: PromoNewsDetail[] = (await getAsync('/api/promo/news')).data;

        // convert some objects
        for(const detail of details) {
            objectizePromoNewsDetail(detail)
            this.UPDATE_DETAIL(detail);
            if(detail.companyNewsId) this.updateCompanyDetail(detail.companyNewsId);
        }

        this.UPDATE_STATUS(details);
    }

    @Action({ rawError: true })
    async updateDetail(id: number) {
        if(typeof(id) !== "number") id = parseInt(id);
        const detail: PromoNewsDetail = (await getAsync('/api/promo/news/' + id)).data;
        objectizePromoNewsDetail(detail);
        this.UPDATE_DETAIL(detail);
        if(detail.companyNewsId) await this.updateCompanyDetail(detail.companyNewsId);
    }

    @Action({ rawError: true })
    async updateCompanyDetail(id: number) {
        if(typeof(id) !== "number") id = parseInt(id);
        const detail: SystemCompanyNewsDetail = (await getAsync('/api/system/company_news/' + id)).data;
        objectizeSystemCompanyNewsDetail(detail);
        this.UPDATE_COMPANY_DETAIL(detail);
    }

    @Action({ rawError: true })
    async getStatusList(): Promise<PromoNewsStatus[]> {
        if(!this.status_whole) {
            await this.updateStatus();
        }
        return this.status;
    }

    @Action({ rawError: true })
    async modifyDetail(detail: PromoNewsDetail) {
        const prev = this.details[detail.id];
        if(!prev) return;

        // extract modification
        let mods: { [key: string]: any } = {};
        for(const key in detail) {
            if((prev as any)[key] !== (detail as any)[key])
                mods[key] = (detail as any)[key];
        }

        if(Object.keys(mods).length > 0) {
            // send
            const result = await patchAsync('/api/promo/news/' + detail.id, mods);
            if(result.data.result == "ok") {
                // apply locally
                this.UPDATE_DETAIL(detail);
                return true;
            }
            else
                return false;
        }
    }

    @Action({ rawError: true })
    async modifyCompanyDetail(detail: SystemCompanyNewsDetail) {
        const prev = this.company_details[detail.id];
        if(!prev) return;

        // extract modification
        let mods: { [key: string]: any } = {};
        for(const key in detail) {
            if((prev as any)[key] !== (detail as any)[key])
                mods[key] = (detail as any)[key];
        }

        if(Object.keys(mods).length > 0) {
            // send
            const result = await patchAsync('/api/system/company_news/' + detail.id, mods);
            if(result.data.result == "ok") {
                // apply locally
                this.UPDATE_COMPANY_DETAIL(detail);
                return true;
            }
            else
                return false;
        }
    }

    @Action({ rawError: true })
    async createDetail(detail: Partial<PromoNewsDetail>): Promise<number> {
        const complete: PromoNewsDetail = (await postAsync('/api/promo/news', detail)).data;
        objectizePromoNewsDetail(complete);
        this.UPDATE_DETAIL(complete);
        return complete.id;
    }

    @Action({ rawError: true })
    async createCompanyDetail(detail: Partial<SystemCompanyNewsDetail>): Promise<number> {
        const complete: SystemCompanyNewsDetail = (await postAsync('/api/system/company_news', detail)).data;
        objectizeSystemCompanyNewsDetail(complete);
        this.UPDATE_COMPANY_DETAIL(complete);
        return complete.id;
    }

    @Action({ rawError: true })
    async getDetail(id: number): Promise<PromoNewsDetail> {
        if(!this.details[id]) await this.updateDetail(id);
        return this.details[id];
    }

    @Action({ rawError: true })
    async getCompanyDetail(id: number): Promise<SystemCompanyNewsDetail> {
        if(!this.company_details[id]) await this.updateCompanyDetail(id);
        return this.company_details[id];
    }
}

export default getModule(PromoNewsStore);
